import React from 'react';
import PropTypes from 'prop-types';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { getBorder, getBorderRadius } from 'Components/utils';

export const PROGRESS_PIE_SIZE_AS_SPACING_MULTIPLIER = 2;

const useStyles = buildUseStyles('SubtleProgressPie', (theme) => ({
  root: {
    ...getBorder(theme, { all: true, color: theme.palette.facet.oceana800 }),
    ...getBorderRadius(theme.radius.full, { all: true }),
    // Draw a semicircle for the first 50% of the pie. This will be obscured when we have less than 50%, but when we have 50%+, it will be helpful
    backgroundImage: `linear-gradient(to right, ${theme.palette.facet.stone500} 50%, ${theme.palette.facet.oceana800} 0)`,
    // Make the circle the perfect size for this use case
    width: theme.spacing(PROGRESS_PIE_SIZE_AS_SPACING_MULTIPLIER),
    height: theme.spacing(PROGRESS_PIE_SIZE_AS_SPACING_MULTIPLIER),

    // This is the second semicircle, which will either cover up the first one with white or add to it with the same color as the first circle
    '&::before': {
      content: "''", // Without this, ::before isn't rendered
      display: 'block',
      height: '100%',

      // Turn it into a semicircle
      marginLeft: '50%',
      borderRadius: '0 100% 100% 0 / 50%',

      // If there's under 50%, we're covering up the left semicircle, if there's 50%+ we're adding to the semicircle. This is done with CSS variables
      backgroundColor: 'var(--inner-background)',
      transform: 'rotate(var(--final-semicircle-rotation)) !important',
      transformOrigin: 'left',
    },
  },
}));

export function SubtleProgressPie({ percentage }) {
  const classes = useStyles();
  const theme = useTheme();
  // There are 2 semicircles drawn.
  // The first one is a simple semicircle that takes up the left half of the circle, but the second one needs to be at the correct angle to add to or subtract from the first angle.
  // It can only have a value up to 50, if percentage is 50% or more then we must take off 50 to end up with a the rotation of just this semicircle (and switch to a different color.
  // Assume it's <50% and we need to use white to hide parts of the first semicircle
  let finalSemicircleRotation = Math.round(percentage) / 100;
  let finalSemicircleBackground = theme.palette.facet.stone500;
  if (finalSemicircleRotation >= 0.5) {
    // This circle is 50%+ so we need to add to it, not take away from it.
    finalSemicircleBackground = theme.palette.facet.oceana800;
    finalSemicircleRotation -= 0.5;
  }
  return (
    <div
      className={classes.root}
      style={{
        '--final-semicircle-rotation': `${finalSemicircleRotation}turn`,
        '--inner-background': finalSemicircleBackground,
      }}
    />
  );
}

SubtleProgressPie.propTypes = {
  percentage: PropTypes.number.isRequired,
};
